import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Flame, ChevronLeft, Youtube, Pencil, Timer, Clock, Copy, Wand2, Loader2, Plus, Users, User, Trash2,
  ArrowDown, Link2, Maximize2, X, Sparkles, Flag, Send, Volume2, Pause,
} from 'lucide-react';
import { toast } from 'sonner';
import { RestTimer, Confetti } from './RestTimer';
import { TonnageBar } from './TonnageBar';
import { unlockAudio } from './soundThemes';
import { useClientSettings, TEXT_SCALE_LEVELS } from '../../../context/ClientSettingsContext';
import { MediaFullscreenViewer } from '../../../components/MediaFullscreenViewer';

/** Formulaire "Signaler un problème" (vidéo/image incorrecte, mauvaise posture...) —
 * envoie un ticket admin (`video_problem_reports`), traité dans le panneau "Signalements". */
const ReportProblemForm = ({ ex, authFetch, onDone, L }) => {
  const [reason, setReason] = useState('');
  const [sending, setSending] = useState(false);
  const submit = async () => {
    if (!reason.trim() || reason.trim().length < 3) { toast.error(L('Décrivez le problème en quelques mots.', 'Describe the issue in a few words.')); return; }
    setSending(true);
    try {
      const res = await authFetch('/api/tracking/video-reports', {
        method: 'POST',
        body: JSON.stringify({
          exercise_name: ex.name, exercise_id: ex.exercise_id, reason: reason.trim(),
          video_url: ex.video_url || null, image_url: ex.image_url || null,
        }),
      });
      if (!res.ok) throw new Error();
      toast.success(L('Merci ! Le problème a été signalé à votre coach.', 'Thanks! The issue was reported to your coach.'));
      onDone();
    } catch {
      toast.error(L("Échec de l'envoi. Réessayez.", 'Failed to send. Try again.'));
    } finally {
      setSending(false);
    }
  };
  return (
    <div className="mt-3 p-3 rounded-xl bg-red-500/[0.06] border border-red-500/25 space-y-2" data-testid="report-problem-form">
      <p className="text-xs font-bold text-red-300">{L('Quel est le problème ?', "What's the problem?")}</p>
      <textarea
        value={reason} onChange={(e) => setReason(e.target.value)} rows={2} autoFocus
        placeholder={L('Ex: la vidéo montre le mauvais exercice, mauvaise posture...', 'Ex: video shows the wrong exercise, wrong form...')}
        className="w-full bg-black/40 border border-white/10 rounded-lg px-2.5 py-2 text-white text-xs placeholder:text-zinc-600 resize-none"
        data-testid="report-problem-reason-input"
      />
      <div className="flex justify-end gap-2">
        <button onClick={onDone} className="text-xs text-zinc-500 hover:text-white px-2 py-1.5" data-testid="report-problem-cancel-btn">{L('Annuler', 'Cancel')}</button>
        <button onClick={submit} disabled={sending} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-500/20 border border-red-500/40 text-red-300 text-xs font-bold disabled:opacity-50" data-testid="report-problem-submit-btn">
          {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} {L('Envoyer', 'Send')}
        </button>
      </div>
    </div>
  );
};

/** Fenêtre agrandie "Voir +" côté client — vidéo (si générée) sinon image en grand,
 * texte d'exécution complet + conseils pro, identique à ce que voit l'admin (jusqu'ici
 * le client ne voyait qu'une image + une note courte pendant sa séance).
 * Taille de texte & contraste élevé pilotés par le réglage global (Profil -> Accessibilité).
 * Zoom plein écran (pincement/molette/double-tap) sur l'image ou la vidéo, et lecture audio
 * (TTS OpenAI, voix au choix) du texte d'exécution + conseils pro. */
const AUDIO_SIZE = { base: 'w-9 h-9', lg: 'w-9 h-9', xl: 'w-9 h-9' };

export const ExerciseDetailExpand = ({ ex, authFetch, onClose, L }) => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const [reporting, setReporting] = useState(false);
  const { textScale, cycleTextScale } = useClientSettings();
  const [fullscreen, setFullscreen] = useState(false);
  const [audioLoading, setAudioLoading] = useState(null); // 'female' | 'male' | null
  const [playingVoice, setPlayingVoice] = useState(null);
  const audioRef = useRef(null);
  useEffect(() => { setReporting(false); setFullscreen(false); stopAudio(); }, [ex]);
  useEffect(() => () => stopAudio(), []);
  const sizeIdx = TEXT_SCALE_LEVELS.indexOf(textScale);

  const stopAudio = () => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    setPlayingVoice(null);
  };

  const playVoice = async (voice) => {
    if (playingVoice === voice) { stopAudio(); return; }
    stopAudio();
    setAudioLoading(voice);
    try {
      const res = await fetch(`${backendUrl}/api/exercises/${ex.exercise_id}/audio?voice=${voice}`, { credentials: 'include' });
      if (!res.ok) throw new Error();
      const data = await res.json();
      const audio = new Audio(`${backendUrl}${data.audio_url}`);
      audio.onended = () => setPlayingVoice(null);
      audioRef.current = audio;
      await audio.play();
      setPlayingVoice(voice);
    } catch {
      toast.error(L("Lecture audio indisponible pour cet exercice.", 'Audio playback unavailable for this exercise.'));
    } finally {
      setAudioLoading(null);
    }
  };

  const mediaSrc = ex?.video_url ? `${backendUrl}${ex.video_url}` : ex?.image_url ? `${backendUrl}${ex.image_url}` : null;

  return (
    <AnimatePresence>
      {ex && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[140] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={onClose} data-testid="exercise-detail-expand-modal"
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
            className="bg-zinc-950 border border-white/10 rounded-3xl w-full max-w-lg max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative w-full aspect-video bg-black">
              {ex.video_url ? (
                <video src={`${backendUrl}${ex.video_url}`} className="w-full h-full object-contain" autoPlay loop muted playsInline controls data-testid="exercise-detail-video" />
              ) : ex.image_url ? (
                <img src={`${backendUrl}${ex.image_url}`} alt={ex.name} className="w-full h-full object-contain cursor-zoom-in" onClick={() => setFullscreen(true)} data-testid="exercise-detail-image" />
              ) : null}
              {mediaSrc && (
                <button onClick={() => setFullscreen(true)} className="absolute top-3 left-3 w-8 h-8 rounded-full bg-black/70 flex items-center justify-center text-white hover:bg-black" title={L('Plein écran', 'Fullscreen')} data-testid="exercise-detail-fullscreen-btn">
                  <Maximize2 className="w-4 h-4" />
                </button>
              )}
              <button onClick={onClose} className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/70 flex items-center justify-center text-white hover:bg-black" data-testid="exercise-detail-close-btn">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <div className="flex items-start justify-between gap-3">
                <h3 className="text-xl sm:text-2xl font-black text-white">{ex.name}</h3>
                <div className="flex items-center gap-2 shrink-0">
                  <div className="flex items-center gap-1 bg-white/5 border border-white/10 rounded-full p-0.5" data-testid="exercise-detail-text-size-control">
                    <button onClick={() => cycleTextScale(-1)}
                      disabled={sizeIdx === 0}
                      className="w-7 h-7 rounded-full flex items-center justify-center text-zinc-300 hover:text-white hover:bg-white/10 disabled:opacity-30 transition-colors text-xs font-bold"
                      title={L('Texte plus petit', 'Smaller text')} data-testid="exercise-detail-text-smaller-btn">
                      A<span className="text-[9px]">−</span>
                    </button>
                    <button onClick={() => cycleTextScale(1)}
                      disabled={sizeIdx === TEXT_SCALE_LEVELS.length - 1}
                      className="w-7 h-7 rounded-full flex items-center justify-center text-zinc-300 hover:text-white hover:bg-white/10 disabled:opacity-30 transition-colors text-sm font-bold"
                      title={L('Texte plus grand', 'Bigger text')} data-testid="exercise-detail-text-bigger-btn">
                      A<span className="text-xs">+</span>
                    </button>
                  </div>
                  {authFetch && (
                    <button onClick={() => setReporting((v) => !v)} className="shrink-0 inline-flex items-center gap-1 text-[11px] font-semibold text-zinc-500 hover:text-red-300 transition-colors" data-testid="report-problem-toggle-btn">
                      <Flag className="w-3.5 h-3.5" /> {L('Signaler', 'Report')}
                    </button>
                  )}
                </div>
              </div>

              {ex.execution && (
                <div className="flex items-center gap-2" data-testid="exercise-detail-audio-controls">
                  <span className="text-[11px] font-semibold text-zinc-500">{L('Écouter :', 'Listen:')}</span>
                  <button onClick={() => playVoice('female')} disabled={audioLoading === 'female'}
                    className={`inline-flex items-center justify-center rounded-full border transition-colors ${AUDIO_SIZE[textScale]} ${playingVoice === 'female' ? 'bg-primary text-white border-primary' : 'bg-white/5 text-zinc-300 border-white/10 hover:border-primary/40'}`}
                    title={L('Voix féminine', 'Female voice')} data-testid="exercise-detail-audio-female-btn">
                    {audioLoading === 'female' ? <Loader2 className="w-4 h-4 animate-spin" /> : playingVoice === 'female' ? <Pause className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                  </button>
                  <button onClick={() => playVoice('male')} disabled={audioLoading === 'male'}
                    className={`inline-flex items-center justify-center rounded-full border transition-colors ${AUDIO_SIZE[textScale]} ${playingVoice === 'male' ? 'bg-primary text-white border-primary' : 'bg-white/5 text-zinc-300 border-white/10 hover:border-primary/40'}`}
                    title={L('Voix masculine', 'Male voice')} data-testid="exercise-detail-audio-male-btn">
                    {audioLoading === 'male' ? <Loader2 className="w-4 h-4 animate-spin" /> : playingVoice === 'male' ? <Pause className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                  </button>
                </div>
              )}

              {ex.execution ? (
                <p className="text-zinc-300 leading-relaxed text-base sm:text-lg" data-testid="exercise-detail-execution">{ex.execution}</p>
              ) : (
                <p className="text-sm text-zinc-500 italic">{L("Texte d'exécution non disponible pour cet exercice.", 'Execution text not available for this exercise.')}</p>
              )}
              {!!(ex.tips || []).length && (
                <div className="space-y-1.5 pt-1">
                  <p className="text-xs font-bold text-primary uppercase tracking-wide flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" /> {L('Conseils pro', 'Pro tips')}</p>
                  {ex.tips.map((t, i) => (
                    <p key={i} className="text-zinc-400 leading-relaxed flex items-start gap-1.5 text-sm sm:text-base"><span className="text-primary">•</span>{t}</p>
                  ))}
                </div>
              )}
              {reporting && authFetch && <ReportProblemForm ex={ex} authFetch={authFetch} onDone={() => setReporting(false)} L={L} />}
            </div>
          </motion.div>
          {fullscreen && mediaSrc && (
            <MediaFullscreenViewer src={mediaSrc} type={ex.video_url ? 'video' : 'image'} onClose={() => setFullscreen(false)} />
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const SET_TYPE_LABELS = {
  straight: 'Séries droites', superset: 'Superset', biset: 'Bi-set', triset: 'Tri-set',
  giant_set: 'Série géante', pyramid_up: 'Pyramidal montant', pyramid_down: 'Pyramidal descendant',
  drop_set: 'Drop Set', rest_pause: 'Rest-Pause', cluster_set: 'Cluster Set',
  pre_exhaust: 'Pré-épuisement', post_exhaust: 'Post-épuisement', twenty_ones: '21s',
  amrap: 'AMRAP', emom: 'EMOM', tempo: 'Tempo', myo_reps: 'Myo-reps',
  circuit: 'Circuit', isometric: 'Isométrique', forced_reps: 'Reps forcées',
};

/** Popup affiché après modification du temps de repos : portée locale (cette séance) ou globale (tout le programme). */
const RestScopeDialog = ({ draft, onChoose, onClose, L }) => (
  <AnimatePresence>
    {draft && (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[120] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="rest-scope-dialog">
        <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-zinc-900 border border-white/10 rounded-3xl p-6 max-w-sm w-full text-center">
          <div className="w-12 h-12 rounded-2xl bg-primary/15 flex items-center justify-center mx-auto mb-4">
            <Timer className="w-6 h-6 text-primary" />
          </div>
          <h3 className="text-white font-bold text-lg mb-1">{draft.newSeconds}s {L('de repos', 'rest')}</h3>
          <p className="text-zinc-400 text-sm mb-6">
            {L('Souhaitez-vous changer ce temps de repos pour toutes les séances de ce programme ou uniquement cette séance ?', 'Do you want to change this rest time for all sessions of this program, or only this session?')}
          </p>
          <div className="space-y-2.5">
            <button onClick={() => onChoose('all')} className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-xl bg-gradient-to-r from-primary to-pink-500 text-white font-bold text-sm hover:scale-[1.02] transition-transform" data-testid="rest-scope-all-btn">
              <Users className="w-4 h-4" /> {L('Toutes les séances de ce programme', 'All sessions of this program')}
            </button>
            <button onClick={() => onChoose('session')} className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-xl bg-white/5 border border-white/10 text-white font-bold text-sm hover:bg-white/10 transition-colors" data-testid="rest-scope-session-btn">
              <User className="w-4 h-4" /> {L('Uniquement cette séance', 'Only this session')}
            </button>
            <button onClick={onClose} className="w-full text-zinc-500 hover:text-white text-xs font-semibold pt-1" data-testid="rest-scope-cancel-btn">
              {L('Annuler', 'Cancel')}
            </button>
          </div>
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
);

/**
 * Exécution d'une séance : saisie poids/reps avec valeurs "fantômes" de la dernière
 * fois, remplissage auto en 1 clic, barres de tonnage animées par exercice + globale,
 * timer de récupération avec décompte sonore, minimisable et modifiable à la volée.
 */
export const SessionRunner = ({
  en, session, occurrenceNumber, editingLog, onBack, onSubmit, onExerciseYoutubeUpdate, onRestTimeGlobalChange, authFetch, programId,
}) => {
  const L = (fr, eng) => (en ? eng : fr);
  const draftKey = !editingLog && programId ? `bu_session_draft_${programId}_${session.session_label}_${occurrenceNumber}` : null;
  const [logs, setLogs] = useState({});
  const [restCtx, setRestCtx] = useState(null); // { seconds, exerciseId, exerciseName }
  const [restOverrides, setRestOverrides] = useState({}); // exerciseId -> secondes (portée: cette séance en cours)
  const [restScopeDraft, setRestScopeDraft] = useState(null); // { exerciseId, exerciseName, newSeconds }
  const [restApplySignal, setRestApplySignal] = useState(null); // { seconds, key } — envoyé au timer une fois la portée résolue
  const [soundTheme, setSoundTheme] = useState(() => localStorage.getItem('bu_sound_theme') || 'classique');
  const [ytEditId, setYtEditId] = useState(null);
  const [ytDraft, setYtDraft] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showLiveConfetti, setShowLiveConfetti] = useState(false);
  const [detailEx, setDetailEx] = useState(null);
  const targetHitRef = useRef(!!editingLog);

  // Restauration d'une séance en cours non terminée (brouillon localStorage) — voir persistance
  // ci-dessous : plus jamais de données perdues en quittant l'écran avant "Terminer la séance".
  useEffect(() => {
    const init = {};
    session.exercises.forEach((ex) => {
      const existing = editingLog?.exercises?.find((e) => e.exercise_id === ex.exercise_id);
      init[ex.exercise_id] = Array.from({ length: ex.sets_count }, (_, i) => ({
        reps: existing?.sets?.[i] ? String(existing.sets[i].reps) : '',
        weight: existing?.sets?.[i] ? String(existing.sets[i].weight) : '',
      }));
    });
    let restored = false;
    if (draftKey) {
      try {
        const saved = JSON.parse(localStorage.getItem(draftKey) || 'null');
        if (saved && typeof saved === 'object') {
          Object.keys(saved).forEach((exId) => {
            if (Array.isArray(saved[exId]) && saved[exId].length > 0) { init[exId] = saved[exId]; restored = true; }
          });
        }
      } catch { /* noop */ }
    }
    setLogs(init);
    if (restored) toast.info(L('Séance en cours restaurée — vos données précédentes ont été conservées.', 'Ongoing session restored — your previous entries were kept.'));
    // Ping "séance démarrée" (silencieux, best-effort) : permet le rappel doux J+1 si jamais
    // terminée. Uniquement pour une nouvelle séance (pas une correction de séance passée).
    if (!editingLog && authFetch && programId) {
      authFetch(`/api/tracking/programs-pro/${programId}/session-started`, { method: 'POST', body: JSON.stringify({ session_label: session.session_label, exercises: [] }) }).catch(() => {});
    }
  }, [session.session_label]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sauvegarde continue du brouillon à chaque saisie : jamais perdu si le client quitte
  // l'écran, met l'app en arrière-plan ou éteint son téléphone avant de valider la séance.
  useEffect(() => {
    if (draftKey && Object.keys(logs).length > 0) {
      try { localStorage.setItem(draftKey, JSON.stringify(logs)); } catch { /* quota storage plein, non bloquant */ }
    }
  }, [logs, draftKey]);

  const changeSoundTheme = (t) => { setSoundTheme(t); localStorage.setItem('bu_sound_theme', t); };

  const updateSet = (exerciseId, idx, field, value) => {
    setLogs((prev) => {
      const next = { ...prev };
      next[exerciseId] = next[exerciseId].map((s, i) => (i === idx ? { ...s, [field]: value } : s));
      return next;
    });
  };

  const duplicatePrevSet = (exerciseId, idx) => {
    setLogs((prev) => {
      const arr = prev[exerciseId];
      const prevSet = arr[idx - 1];
      if (!prevSet) return prev;
      const next = { ...prev };
      next[exerciseId] = arr.map((s, i) => (i === idx ? { ...prevSet } : s));
      return next;
    });
  };

  const addExtraSet = (exerciseId) => {
    setLogs((prev) => ({ ...prev, [exerciseId]: [...prev[exerciseId], { reps: '', weight: '' }] }));
    toast.success(L('Série supplémentaire ajoutée pour cette séance.', 'Extra set added for this session.'));
  };

  const removeExtraSet = (exerciseId, idx) => {
    setLogs((prev) => ({ ...prev, [exerciseId]: prev[exerciseId].filter((_, i) => i !== idx) }));
  };

  const addExtraRound = (members) => {
    setLogs((prev) => {
      const next = { ...prev };
      members.forEach((m) => { next[m.exercise_id] = [...next[m.exercise_id], { reps: '', weight: '' }]; });
      return next;
    });
    toast.success(L('Round supplémentaire ajouté pour ce bloc.', 'Extra round added for this block.'));
  };

  const removeExtraRound = (members, roundIdx) => {
    setLogs((prev) => {
      const next = { ...prev };
      members.forEach((m) => { next[m.exercise_id] = next[m.exercise_id].filter((_, i) => i !== roundIdx); });
      return next;
    });
  };

  const fillWithGhost = (ex) => {
    if (!ex.last_sets?.length) return;
    setLogs((prev) => {
      const next = { ...prev };
      next[ex.exercise_id] = prev[ex.exercise_id].map((s, i) => {
        const ghost = ex.last_sets[i];
        if (s.reps || s.weight || !ghost) return s;
        const repsVal = ex.is_time_based ? (ghost.duration_seconds ?? ghost.reps) : ghost.reps;
        return { reps: String(repsVal), weight: String(ghost.weight) };
      });
      return next;
    });
    toast.success(L('Valeurs de la dernière séance appliquées.', 'Last session values applied.'));
  };

  const liveTonnage = (exerciseId) => (logs[exerciseId] || []).reduce((acc, s) => acc + ((+s.reps || 0) * (+s.weight || 0)), 0);
  const totalTonnage = () => Object.keys(logs).reduce((acc, exId) => acc + liveTonnage(exId), 0);

  const sessionTarget = session.tonnage_target;
  const currentTonnage = totalTonnage();
  useEffect(() => {
    if (targetHitRef.current || !sessionTarget) return;
    if (currentTonnage >= sessionTarget) {
      targetHitRef.current = true;
      setShowLiveConfetti(true);
      toast.success(L('Objectif de tonnage de la séance atteint ! 🎉', 'Session tonnage target reached! 🎉'));
      setTimeout(() => setShowLiveConfetti(false), 2600);
    }
  }, [currentTonnage, sessionTarget]); // eslint-disable-line react-hooks/exhaustive-deps

  const getRestSeconds = (ex) => restOverrides[ex.exercise_id] ?? ex.rest_seconds;

  const finishSet = (ex, isLast, displayName) => {
    if (isLast) return;
    unlockAudio(); // geste utilisateur direct — garantit que le contexte audio est actif avant le décompte
    setRestApplySignal(null);
    setRestCtx({ seconds: getRestSeconds(ex), exerciseId: ex.exercise_id, exerciseName: displayName || ex.name, isCardio: ex.muscle_group === 'Full-Body / Cardio' });
  };

  const resolveRestScope = async (scope) => {
    const { exerciseId, newSeconds } = restScopeDraft;
    setRestOverrides((prev) => ({ ...prev, [exerciseId]: newSeconds }));
    setRestApplySignal({ seconds: newSeconds, key: Date.now() });
    if (scope === 'all' && onRestTimeGlobalChange) {
      try {
        await onRestTimeGlobalChange(exerciseId, newSeconds);
        toast.success(L('Temps de repos mis à jour pour toutes les séances de ce programme.', 'Rest time updated for all sessions of this program.'));
      } catch {
        toast.error(L('Erreur lors de la sauvegarde, changement appliqué à cette séance seulement.', 'Save error, change applied to this session only.'));
      }
    } else {
      toast.success(L('Temps de repos mis à jour pour cette séance.', 'Rest time updated for this session.'));
    }
    setRestScopeDraft(null);
  };

  const saveYoutubeOverride = async (exerciseId) => {
    if (!ytDraft.trim()) { setYtEditId(null); return; }
    await onExerciseYoutubeUpdate(exerciseId, ytDraft.trim());
    setYtEditId(null);
  };

  const submitSession = async () => {
    setSubmitting(true);
    const exercises = session.exercises.map((ex) => ({
      exercise_id: ex.exercise_id,
      name: ex.name,
      sets: (logs[ex.exercise_id] || [])
        .map((s, i) => (ex.is_time_based
          ? { set_number: i + 1, reps: 0, weight: +s.weight || 0, duration_seconds: +s.reps || 0 }
          : { set_number: i + 1, reps: +s.reps || 0, weight: +s.weight || 0 }))
        .filter((s) => (ex.is_time_based ? s.duration_seconds > 0 : s.reps > 0)),
    })).filter((ex) => ex.sets.length > 0);

    if (exercises.length === 0) {
      toast.error(L('Renseignez au moins une série avant de terminer.', 'Log at least one set before finishing.'));
      setSubmitting(false);
      return;
    }
    const ok = await onSubmit(exercises);
    if (ok !== false && draftKey) {
      try { localStorage.removeItem(draftKey); } catch { /* noop */ }
    }
    setSubmitting(false);
  };

  const totalSets = Object.values(logs).reduce((a, arr) => a + arr.length, 0);
  const doneSets = Object.values(logs).reduce((a, arr) => a + arr.filter((s) => s.reps && s.weight).length, 0);

  // Regroupe les exercices consécutifs partageant un group_id (superset/bi-set/tri-set/
  // circuit/pré-post-épuisement) en un seul bloc d'exécution round-robin.
  const blocks = [];
  session.exercises.forEach((ex) => {
    const last = blocks[blocks.length - 1];
    if (ex.group_id && last && last.groupId === ex.group_id) last.members.push(ex);
    else blocks.push({ groupId: ex.group_id || null, members: [ex] });
  });

  const renderExerciseHeader = (ex, compact) => (
    <>
      {ex.image_url && (
        <div className={`relative w-full ${compact ? 'h-32' : 'h-48'} rounded-xl overflow-hidden border border-primary/40 shadow-[0_0_24px_-4px_rgba(217,70,239,0.55)] mb-3 bg-zinc-950 flex items-center justify-center`}>
          <img src={`${process.env.REACT_APP_BACKEND_URL}${ex.image_url}`} alt={ex.name} loading="lazy"
            className="max-w-full max-h-full w-full h-full object-contain"
            data-testid={`exercise-execution-image-${ex.exercise_id}`} />
        </div>
      )}
      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
        <div>
          <p className="text-white font-bold">{ex.name}</p>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            {!compact && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-primary/20 text-primary">{SET_TYPE_LABELS[ex.set_type] || ex.set_type}</span>}
            <span className="text-[10px] text-zinc-500">{ex.reps_scheme}{ex.is_time_based ? '' : ' reps'} · {ex.sets_count} séries</span>
            {!compact && <span className="text-[10px] text-zinc-500 inline-flex items-center gap-0.5"><Clock className="w-3 h-3" /> {getRestSeconds(ex)}s</span>}
            {ex.tempo && <span className="text-[10px] text-zinc-400 inline-flex items-center gap-0.5" data-testid={`tempo-badge-${ex.exercise_id}`}><Timer className="w-3 h-3" /> {ex.tempo}</span>}
          </div>
          {ex.notes && <p className="text-[11px] text-zinc-400 italic mt-1">{ex.notes}</p>}
          {ex.execution && (
            <p className="text-[11px] text-zinc-500 mt-1.5 line-clamp-2" data-testid={`exercise-mini-summary-${ex.exercise_id}`}>
              {ex.execution.length > 100 ? `${ex.execution.slice(0, 100).trim()}…` : ex.execution}
            </p>
          )}
          {(ex.image_url || ex.execution) && (
            <button onClick={() => setDetailEx(ex)}
              className="inline-flex items-center gap-1.5 mt-2 px-3 py-1.5 rounded-full text-[11px] font-black text-white bg-gradient-to-r from-primary to-fuchsia-500 shadow-[0_0_16px_-2px_rgba(217,70,239,0.7)] hover:scale-105 active:scale-95 transition-transform animate-pulse-slow"
              data-testid={`exercise-view-more-${ex.exercise_id}`}>
              <Maximize2 className="w-3 h-3" /> {L('Voir + (vidéo, conseils pro)', 'See more (video, pro tips)')}
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          {ex.youtube_url && (
            <a href={ex.youtube_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-red-400 hover:text-red-300" data-testid={`youtube-link-${ex.exercise_id}`}>
              <Youtube className="w-4 h-4" /> {L('Voir la vidéo', 'Watch video')}
            </a>
          )}
          <button onClick={() => { setYtEditId(ex.exercise_id); setYtDraft(ex.youtube_url || ''); }} className="text-zinc-500 hover:text-white" data-testid={`edit-youtube-${ex.exercise_id}`}>
            <Pencil className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
      {ytEditId === ex.exercise_id && (
        <div className="flex items-center gap-2 mb-3">
          <input value={ytDraft} onChange={(e) => setYtDraft(e.target.value)} placeholder="https://youtube.com/..." className="flex-1 bg-zinc-800 border border-white/10 rounded-lg px-2 py-1.5 text-white text-xs" data-testid={`youtube-draft-input-${ex.exercise_id}`} />
          <button onClick={() => saveYoutubeOverride(ex.exercise_id)} className="text-primary text-xs font-bold" data-testid={`save-youtube-${ex.exercise_id}`}>OK</button>
        </div>
      )}
      {ex.last_sets?.length > 0 && (
        <button onClick={() => fillWithGhost(ex)} className="mb-2.5 inline-flex items-center gap-1.5 text-[11px] font-bold text-purple-300 hover:text-purple-200 border border-purple-500/30 rounded-full px-2.5 py-1 transition-colors" data-testid={`ghost-fill-btn-${ex.exercise_id}`}>
          <Wand2 className="w-3 h-3" /> {L('Reprendre les valeurs de la dernière fois', 'Reuse last session values')}
        </button>
      )}
    </>
  );

  return (
    <div className="space-y-5" data-testid="session-execution-view">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <button onClick={onBack} className="inline-flex items-center gap-1.5 text-zinc-400 hover:text-white text-sm" data-testid="back-to-program-btn">
          <ChevronLeft className="w-4 h-4" /> {L('Retour', 'Back')}
        </button>
        <div className="flex items-center gap-2">
          {!editingLog && (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide bg-amber-500/15 text-amber-300 border border-amber-500/30" data-testid="session-in-progress-badge">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" /> {L('Séance en cours', 'Session in progress')}
            </span>
          )}
          <span className="text-xs font-mono text-zinc-500">{L('Séance', 'Session')} {occurrenceNumber}</span>
        </div>
      </div>

      <p className="text-[11px] text-amber-300/80 bg-amber-500/[0.06] border border-amber-500/20 rounded-lg px-3 py-2 flex items-start gap-1.5" data-testid="session-draft-hint">
        <Wand2 className="w-3.5 h-3.5 shrink-0 mt-px" />
        {L(
          'Vos données sont sauvegardées automatiquement au fur et à mesure, même si vous quittez cet écran. La séance ne sera comptée dans votre historique qu\u2019après avoir appuyé sur « Terminer la séance ».',
          'Your entries are saved automatically as you go, even if you leave this screen. The session will only count in your history once you tap "Finish session".',
        )}
      </p>

      <div className="rounded-2xl bg-white/[0.035] backdrop-blur-xl border border-white/10 shadow-[0_0_30px_-12px_rgba(217,70,239,0.25)] p-4">
        <h2 className="text-2xl font-black text-white flex items-center gap-2 mb-3">
          <Flame className="w-6 h-6 text-primary" /> {session.session_label}
        </h2>
        <TonnageBar current={totalTonnage()} target={sessionTarget} label={L('Tonnage total de la séance', 'Total session tonnage')} big unit="kg" />
        <div className="h-1 bg-zinc-800 rounded-full overflow-hidden mt-3">
          <motion.div className="h-full bg-gradient-to-r from-purple-500 to-primary" animate={{ width: `${Math.min(100, (doneSets / totalSets) * 100)}%` }} />
        </div>
      </div>

      <p className="text-[11px] text-zinc-500 flex items-center gap-1.5 px-1" data-testid="bodyweight-hint">
        💡 {L("Exercice au poids du corps ? Entrez « 1 » dans la case Poids.", 'Bodyweight exercise? Enter "1" in the Weight field.')}
      </p>

      {blocks.map((block, blockIdx) => {
        const isLastBlock = blockIdx === blocks.length - 1;

        if (block.members.length === 1) {
          const ex = block.members[0];
          return (
            <div key={ex.exercise_id} className="rounded-2xl bg-white/[0.035] backdrop-blur-xl border border-white/10 shadow-[0_0_30px_-12px_rgba(217,70,239,0.25)] p-4" data-testid={`exercise-execution-${ex.exercise_id}`}>
              {renderExerciseHeader(ex, false)}
              <div className="space-y-2">
                <div className="flex items-center gap-2 px-1 text-[10px] font-bold uppercase tracking-wider text-zinc-500">
                  <span className="w-6" />
                  <span className="w-20 text-center">{L('Poids (kg)', 'Weight (kg)')}</span>
                  <span className="w-16 text-center">{ex.is_time_based ? L('Temps (s)', 'Time (s)') : L('Reps', 'Reps')}</span>
                </div>
                {(logs[ex.exercise_id] || []).map((s, sIdx) => {
                  const ghost = ex.last_sets?.[sIdx];
                  const ghostReps = ghost ? (ex.is_time_based ? (ghost.duration_seconds ?? ghost.reps) : ghost.reps) : null;
                  const isLastOfExercise = sIdx === (logs[ex.exercise_id] || []).length - 1;
                  const isLastOfSession = isLastBlock && isLastOfExercise;
                  return (
                    <div key={sIdx} className="flex items-center gap-2">
                      <span className="w-6 text-center text-xs text-zinc-500 font-bold">{sIdx + 1}</span>
                      <input type="number" placeholder={ghost ? String(ghost.weight) : '—'} value={s.weight}
                        onChange={(e) => updateSet(ex.exercise_id, sIdx, 'weight', e.target.value)}
                        className="w-20 bg-zinc-800 border border-white/10 rounded-lg px-2 py-2 text-white text-sm text-center placeholder:text-zinc-600" data-testid={`set-weight-input-${ex.exercise_id}-${sIdx}`} />
                      <input type="number" placeholder={ghostReps != null ? String(ghostReps) : '—'} value={s.reps}
                        onChange={(e) => updateSet(ex.exercise_id, sIdx, 'reps', e.target.value)}
                        className="w-16 bg-zinc-800 border border-white/10 rounded-lg px-2 py-2 text-white text-sm text-center placeholder:text-zinc-600" data-testid={`set-reps-input-${ex.exercise_id}-${sIdx}`} />
                      {sIdx > 0 && (
                        <button onClick={() => duplicatePrevSet(ex.exercise_id, sIdx)} title={L('Dupliquer la série précédente', 'Duplicate previous set')} className="p-2 rounded-lg bg-white/5 text-zinc-400 hover:text-white hover:bg-white/10 transition-colors shrink-0" data-testid={`duplicate-set-btn-${ex.exercise_id}-${sIdx}`}>
                          <Copy className="w-3.5 h-3.5" />
                        </button>
                      )}
                      {sIdx >= ex.sets_count && (
                        <button onClick={() => removeExtraSet(ex.exercise_id, sIdx)} title={L('Supprimer cette série ajoutée', 'Delete this added set')} className="p-2 rounded-lg bg-red-500/10 text-red-400 hover:text-red-300 hover:bg-red-500/20 transition-colors shrink-0" data-testid={`remove-extra-set-btn-${ex.exercise_id}-${sIdx}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                      <button
                        onClick={() => finishSet(ex, isLastOfSession)}
                        disabled={!s.weight || !s.reps}
                        title={L(`Valider et démarrer ${getRestSeconds(ex)}s de repos`, `Validate and start ${getRestSeconds(ex)}s rest`)}
                        className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-primary/20 text-primary disabled:opacity-30 disabled:cursor-not-allowed hover:bg-primary/30 transition-colors font-bold text-xs"
                        data-testid={`validate-set-btn-${ex.exercise_id}-${sIdx}`}
                      >
                        <Timer className="w-3.5 h-3.5" /> {getRestSeconds(ex)}s
                      </button>
                    </div>
                  );
                })}
                <button onClick={() => addExtraSet(ex.exercise_id)} className="inline-flex items-center gap-1.5 text-[11px] font-bold text-zinc-400 hover:text-white mt-1" data-testid={`add-extra-set-btn-${ex.exercise_id}`}>
                  <Plus className="w-3.5 h-3.5" /> {L('Ajouter une série', 'Add a set')}
                </button>
                <div className="pt-1.5">
                  <TonnageBar current={liveTonnage(ex.exercise_id)} target={ex.tonnage_target} label={L('Tonnage exercice', 'Exercise tonnage')} />
                </div>
              </div>
            </div>
          );
        }

        // ── Bloc groupé (superset/bi-set/tri-set/série géante/circuit/pré-post-épuisement) :
        // remplissage round-robin — un seul repos partagé après le tour complet.
        const { members } = block;
        const last = members[members.length - 1];
        const rounds = Math.max(...members.map((m) => (logs[m.exercise_id] || []).length));
        const minPrescribed = Math.min(...members.map((m) => m.sets_count));
        const groupLabel = `${SET_TYPE_LABELS[members[0].set_type] || 'Groupe'} : ${members.map((m) => m.name).join(' + ')}`;
        return (
          <div key={block.groupId} className="rounded-2xl border-2 border-amber-500/40 bg-gradient-to-br from-amber-500/[0.06] to-white/[0.02] backdrop-blur-xl p-4 space-y-3" data-testid={`group-block-${block.groupId}`}>
            <span className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-wide px-2.5 py-1 rounded-full bg-amber-500/20 text-amber-300" data-testid={`group-block-badge-${block.groupId}`}>
              <Link2 className="w-3 h-3" /> {SET_TYPE_LABELS[members[0].set_type] || members[0].set_type} · {members.length} {L('exercices liés', 'linked exercises')}
            </span>

            {members.map((m, mi) => (
              <div key={m.exercise_id} className={mi > 0 ? 'pt-3 border-t border-white/5' : ''}>
                {renderExerciseHeader(m, true)}
              </div>
            ))}

            <div className="flex items-center gap-2 px-1 text-[10px] font-bold uppercase tracking-wider text-zinc-500">
              <span className="w-20 shrink-0" />
              <span className="w-16 text-center">{L('Poids', 'Weight')}</span>
              <span className="w-14 text-center">{L('Reps', 'Reps')}</span>
            </div>
            <div className="space-y-2.5">
              {Array.from({ length: rounds }).map((_, r) => {
                const isLastRound = r === rounds - 1;
                return (
                  <div key={r} className="rounded-xl bg-black/25 border border-white/5 p-2.5 space-y-1.5" data-testid={`group-round-${block.groupId}-${r}`}>
                    <div className="flex items-center justify-between px-0.5">
                      <p className="text-[10px] font-bold text-amber-400 uppercase tracking-wide">{L('Round', 'Round')} {r + 1}/{rounds}</p>
                      {r >= minPrescribed && (
                        <button onClick={() => removeExtraRound(members, r)} title={L('Supprimer ce round ajouté', 'Delete this added round')} className="p-1 rounded-lg bg-red-500/10 text-red-400 hover:text-red-300 hover:bg-red-500/20 transition-colors" data-testid={`remove-extra-round-btn-${block.groupId}-${r}`}>
                          <Trash2 className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                    {members.map((m, mi) => {
                      const s = (logs[m.exercise_id] || [])[r];
                      if (!s) return null;
                      const isLastMember = mi === members.length - 1;
                      const isLastOfSession = isLastBlock && isLastRound && isLastMember;
                      const ghost = m.last_sets?.[r];
                      const ghostReps = ghost ? (m.is_time_based ? (ghost.duration_seconds ?? ghost.reps) : ghost.reps) : null;
                      return (
                        <div key={m.exercise_id} className="flex items-center gap-2">
                          <span className="w-20 truncate text-[11px] text-zinc-400 font-semibold shrink-0" title={m.name}>{m.name}</span>
                          <input type="number" placeholder={ghost ? String(ghost.weight) : '—'} value={s.weight}
                            onChange={(e) => updateSet(m.exercise_id, r, 'weight', e.target.value)}
                            className="w-16 bg-zinc-800 border border-white/10 rounded-lg px-2 py-2 text-white text-sm text-center placeholder:text-zinc-600" data-testid={`set-weight-input-${m.exercise_id}-${r}`} />
                          <input type="number" placeholder={ghostReps != null ? String(ghostReps) : '—'} value={s.reps}
                            onChange={(e) => updateSet(m.exercise_id, r, 'reps', e.target.value)}
                            className="w-14 bg-zinc-800 border border-white/10 rounded-lg px-2 py-2 text-white text-sm text-center placeholder:text-zinc-600" data-testid={`set-reps-input-${m.exercise_id}-${r}`} />
                          {r > 0 && (
                            <button onClick={() => duplicatePrevSet(m.exercise_id, r)} title={L('Dupliquer le round précédent', 'Duplicate previous round')} className="p-2 rounded-lg bg-white/5 text-zinc-400 hover:text-white hover:bg-white/10 transition-colors shrink-0" data-testid={`duplicate-set-btn-${m.exercise_id}-${r}`}>
                              <Copy className="w-3.5 h-3.5" />
                            </button>
                          )}
                          {isLastMember ? (
                            <button
                              onClick={() => finishSet(last, isLastOfSession, groupLabel)}
                              disabled={!s.weight || !s.reps}
                              title={L(`Valider et démarrer ${getRestSeconds(last)}s de repos`, `Validate and start ${getRestSeconds(last)}s rest`)}
                              className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-amber-500/20 text-amber-300 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-amber-500/30 transition-colors font-bold text-xs"
                              data-testid={`validate-set-btn-${m.exercise_id}-${r}`}
                            >
                              <Timer className="w-3.5 h-3.5" /> {getRestSeconds(last)}s
                            </button>
                          ) : (
                            <span className="flex-1 flex items-center justify-center text-zinc-600" title={L('Enchaînez directement, sans repos', 'Go straight on, no rest')}>
                              <ArrowDown className="w-3.5 h-3.5 -rotate-90" />
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
              <button onClick={() => addExtraRound(members)} className="inline-flex items-center gap-1.5 text-[11px] font-bold text-zinc-400 hover:text-white mt-1" data-testid={`add-extra-round-btn-${block.groupId}`}>
                <Plus className="w-3.5 h-3.5" /> {L('Ajouter un round', 'Add a round')}
              </button>
            </div>

            <div className="grid gap-1.5 pt-1">
              {members.map((m) => (
                <TonnageBar key={m.exercise_id} current={liveTonnage(m.exercise_id)} target={m.tonnage_target} label={`${L('Tonnage', 'Tonnage')} · ${m.name}`} />
              ))}
            </div>
          </div>
        );
      })}

      <button onClick={submitSession} disabled={submitting} className="w-full py-4 rounded-2xl bg-gradient-to-r from-primary to-pink-500 text-white font-black text-lg hover:scale-[1.02] transition-transform disabled:opacity-50" data-testid="finish-session-btn">
        {submitting ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : (editingLog ? L('Enregistrer les modifications', 'Save changes') : L('Terminer la séance', 'Finish session'))}
      </button>

      <AnimatePresence>
        {restCtx != null && (
          <RestTimer
            seconds={restCtx.seconds}
            exerciseName={restCtx.exerciseName}
            isCardio={restCtx.isCardio}
            en={en} soundTheme={soundTheme} onChangeSoundTheme={changeSoundTheme}
            onDone={() => setRestCtx(null)}
            onChangeRestTime={(newSeconds) => setRestScopeDraft({ exerciseId: restCtx.exerciseId, exerciseName: restCtx.exerciseName, newSeconds })}
            awaitingScopeChoice={restScopeDraft != null}
            applySignal={restApplySignal}
          />
        )}
      </AnimatePresence>
      <RestScopeDialog draft={restScopeDraft} onChoose={resolveRestScope} onClose={() => setRestScopeDraft(null)} L={L} />
      {showLiveConfetti && (
        <div data-testid="live-tonnage-confetti"><Confetti /></div>
      )}
      <ExerciseDetailExpand ex={detailEx} authFetch={authFetch} onClose={() => setDetailEx(null)} L={L} />
    </div>
  );
};

export default SessionRunner;
